import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { BehaviorSubject, Observable } from 'rxjs';

export type ThemeMode = 'light' | 'dark' | 'auto';

const THEME_STORAGE_KEY = 'vessel-theme-mode';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private storage: Storage | null = null;
  private currentTheme$ = new BehaviorSubject<ThemeMode>('light');
  private systemPrefersDark: MediaQueryList | null = null;
  private systemPreferenceListener: ((e: MediaQueryListEvent) => void) | null = null;

  constructor(private storageService: Storage) {
    this.init();
  }

  private async init(): Promise<void> {
    this.storage = await this.storageService.create();

    // Setup system preference listener
    this.systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)');
    this.systemPreferenceListener = (e: MediaQueryListEvent) => {
      if (this.currentTheme$.value === 'auto') {
        this.applyTheme('auto');
      }
    };
    this.systemPrefersDark.addEventListener('change', this.systemPreferenceListener);

    // Load saved theme preference
    const savedTheme = await this.getStoredTheme();
    if (savedTheme) {
      this.setTheme(savedTheme);
    } else {
      // Default to light
      this.setTheme('light');
    }
  }

  /**
   * Get the current theme mode as an observable
   */
  getTheme$(): Observable<ThemeMode> {
    return this.currentTheme$.asObservable();
  }

  /**
   * Get the current theme mode value
   */
  getCurrentTheme(): ThemeMode {
    return this.currentTheme$.value;
  }

  /**
   * Set the theme mode and apply it
   */
  async setTheme(mode: ThemeMode): Promise<void> {
    this.currentTheme$.next(mode);
    this.applyTheme(mode);
    await this.storeTheme(mode);
  }

  /**
   * Check if dark mode is currently active (regardless of setting)
   */
  isDarkModeActive(): boolean {
    return document.body.classList.contains('dark');
  }

  /**
   * Apply the theme to the document
   */
  private applyTheme(mode: ThemeMode): void {
    const shouldBeDark = this.shouldApplyDarkMode(mode);

    // Remove both classes first
    document.body.classList.remove('dark', 'light');

    if (mode === 'dark') {
      document.body.classList.add('dark');
    } else if (mode === 'light') {
      document.body.classList.add('light');
    }
    // For 'auto' mode, don't add any class - let media queries handle it

    // Also explicitly set the dark class based on computed state
    // This ensures immediate visual feedback
    if (shouldBeDark && mode !== 'light') {
      document.body.classList.add('dark');
    }
  }

  /**
   * Determine if dark mode should be applied based on the mode setting
   */
  private shouldApplyDarkMode(mode: ThemeMode): boolean {
    if (mode === 'dark') {
      return true;
    }
    if (mode === 'light') {
      return false;
    }
    // Auto mode - check system preference
    return this.systemPrefersDark?.matches ?? false;
  }

  /**
   * Store the theme preference in local storage
   */
  private async storeTheme(mode: ThemeMode): Promise<void> {
    if (!this.storage) {
      this.storage = await this.storageService.create();
    }
    await this.storage?.set(THEME_STORAGE_KEY, mode);
  }

  /**
   * Get the stored theme preference from local storage
   */
  private async getStoredTheme(): Promise<ThemeMode | null> {
    if (!this.storage) {
      this.storage = await this.storageService.create();
    }
    const stored = await this.storage?.get(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'auto') {
      return stored;
    }
    return null;
  }

  /**
   * Clean up listeners when service is destroyed
   */
  destroy(): void {
    if (this.systemPrefersDark && this.systemPreferenceListener) {
      this.systemPrefersDark.removeEventListener('change', this.systemPreferenceListener);
    }
  }
}
